import { TIPO_INSPECAO } from "@/contexts/cadastro/estabelecimento";
import { useRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/hooks/useRelatorioAM";
import { STATUS_ALTERNATIVA_RAM } from "@/contexts/fiscalizacao/relatorioAM/model/STATUS_ALTERNATIVA_RAM";
import { PageLoader } from "@/shared/components/PageLoader/PageLoader";
import { cpfMask } from "@/shared/utils/cpfMask";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Grid,
  Input,
  List,
  Result,
  Row,
  Space,
  Tag,
  TimePicker,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";

export function VisualizacaoRAM() {
  const screens = Grid.useBreakpoint();
  const { id } = useParams<{ id: string }>();
  const { getRelatorioAMByIdApi, getAnexoApi } = useRelatorioAM();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const {
    data: relatorio,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["RAM_VISUALIZACAO", id],
    enabled: !!id,
    queryFn: async () => {
      const response = await getRelatorioAMByIdApi(Number(id!));
      return response;
    },
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenAnexo = async (nomeUpload?: string) => {
    if (!nomeUpload) return;

    try {
      const anexoBlob = await getAnexoApi(nomeUpload);
      saveAs(anexoBlob, nomeUpload || "anexo");
    } catch (error) {
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possivel baixar o anexo.",
        description: messageError,
        key: "notification-error",
      });
    }
  };

  const getCorPeso = (peso: string) => {
    switch (peso) {
      case "Leve":
        return "green";
      case "Moderado":
        return "orange";
      case "Grave":
        return "red";
      case "Gravíssimo":
        return "purple";
      default:
        return "default";
    }
  };

  const getStatusMap = (status: string) => {
    const statusMap = {
      ASSINADO_DIGITALMENTE: "Assinado Digitalmente",
      ASSINADO_FISICAMENTE: "Assinado Fisicamente",
      IMPOSSIBILITADO: "Impossibilitado",
      AUSENTE: "Ausente",
      RECUSADO: "Recusado",
      NAO_ALFABETIZADO: "Não Alfabetizado",
    } as const;

    return statusMap[status as keyof typeof statusMap] || status;
  };

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Visualização de Relatório de Auditoria e Monitoria";
  }, []);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !relatorio) {
    return (
      <Result
        status="500"
        title="Parece que aconteceu algum problema com o servidor."
        subTitle="Tente novamente mais tarde ou entre em contato com o suporte."
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Voltar
            </Button>
            <Button onClick={() => refetch()}>Tentar Novamente</Button>
          </Space>
        }
      />
    );
  }

  // Agrupar itens por seção para exibição
  const itensPorSecao =
    relatorio?.itens.reduce(
      (acc, item) => {
        const secaoNome = item.secao?.nome ?? "";
        if (!acc[secaoNome]) {
          acc[secaoNome] = [];
        }
        acc[secaoNome].push(item);
        return acc;
      },
      {} as Record<string, typeof relatorio.itens>
    ) || {};

  return (
    <>
      <Typography.Title
        level={screens.xs ? 4 : 3}
        style={{ textAlign: "center" }}
      >
        Relatório de Auditoria e Monitoria {relatorio?.id}
      </Typography.Title>
      <Divider />

      <Form layout="vertical">
        {/* Dados Gerais */}
        <Divider orientation="left" plain>
          <Typography.Title level={5}>Dados Gerais</Typography.Title>
        </Divider>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Estabelecimento">
              <Input
                disabled
                value={`${relatorio?.estabelecimento?.numeroInspecao} - ${relatorio.estabelecimento ? TIPO_INSPECAO[relatorio.estabelecimento.tipoInspecao] : "SISE"} - ${relatorio?.estabelecimento?.nome}`}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Formulário">
              <Input disabled value={relatorio?.formulario?.nome} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Proprietário">
              <Input disabled value={relatorio?.proprietario?.nome} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Inspetor/Monitor">
              <Input
                disabled
                value={
                  relatorio?.inspetor?.nome || relatorio?.inspetor?.nomePessoa
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Responsável Técnico">
              <Input disabled value={relatorio?.responsavelTecnico} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Data da Monitoria">
              <DatePicker
                disabled
                format="DD/MM/YYYY"
                value={
                  relatorio?.dataMonitoria
                    ? dayjs(relatorio.dataMonitoria)
                    : undefined
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {relatorio?.observacao && (
            <Col xs={24}>
              <Form.Item label="Observação">
                <Input.TextArea
                  disabled
                  rows={3}
                  value={relatorio.observacao}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Fiscalizações */}
        {relatorio?.fiscalizacoes && relatorio.fiscalizacoes.length > 0 && (
          <>
            <Divider orientation="left" plain>
              <Typography.Title level={5}>
                Fiscalizações Realizadas
              </Typography.Title>
            </Divider>

            <Row gutter={[16, 16]}>
              {relatorio.fiscalizacoes.map((fiscalizacao, index) => (
                <Col xs={24} key={index}>
                  <Row
                    gutter={16}
                    align="middle"
                    style={{
                      marginBottom: 16,
                      padding: 16,
                      border: "1px solid #f0f0f0",
                      borderRadius: 6,
                    }}
                  >
                    <Col xs={24} sm={6}>
                      <Form.Item label={`Fiscalização ${index + 1} - Data`}>
                        <DatePicker
                          disabled
                          format="DD/MM/YYYY"
                          value={
                            fiscalizacao.data
                              ? dayjs(fiscalizacao.data)
                              : undefined
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={6}>
                      <Form.Item label="Hora Inicial">
                        <TimePicker
                          disabled
                          format="HH:mm"
                          value={
                            fiscalizacao.horaInicial
                              ? dayjs(fiscalizacao.horaInicial, "HH:mm")
                              : undefined
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={6}>
                      <Form.Item label="Hora Final">
                        <TimePicker
                          disabled
                          format="HH:mm"
                          value={
                            fiscalizacao.horaFinal
                              ? dayjs(fiscalizacao.horaFinal, "HH:mm")
                              : undefined
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={6}>
                      <Form.Item label="Placa">
                        <Input disabled value={fiscalizacao.placa} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* Questionários */}
        {Object.keys(itensPorSecao).length > 0 && (
          <>
            <Divider orientation="left" plain>
              <Typography.Title level={5}>Questionários</Typography.Title>
            </Divider>

            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Collapse
                  items={Object.entries(itensPorSecao).map(
                    ([secaoNome, itens]) => ({
                      key: secaoNome,
                      label: (
                        <Typography.Text strong>
                          {secaoNome} ({itens.length}{" "}
                          {itens.length === 1 ? "pergunta" : "perguntas"})
                        </Typography.Text>
                      ),
                      children: (
                        <>
                          {itens
                            .filter(
                              (perg) =>
                                perg.pergunta?.visivel && perg.pergunta?.ativo
                            )
                            .map((item, index) => (
                              <div
                                key={`${item.pergunta?.id}-${index}`}
                                style={{
                                  marginBottom: 24,
                                  padding: 16,
                                  border: "1px solid #f0f0f0",
                                  borderRadius: 6,
                                }}
                              >
                                <div style={{ marginBottom: 12 }}>
                                  <Typography.Title
                                    level={5}
                                    style={{ margin: 0 }}
                                  >
                                    {index + 1}. {item.pergunta?.nome}
                                  </Typography.Title>

                                  <div
                                    style={{
                                      marginTop: 8,
                                      display: "flex",
                                      gap: 8,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Tag color={getCorPeso(item.peso || "")}>
                                      Peso: {item.peso}
                                    </Tag>
                                    {item.alternativa && (
                                      <Tag
                                        color={
                                          item.alternativa?.tipo ===
                                          "CONFORMIDADE"
                                            ? "green"
                                            : item.alternativa?.tipo ===
                                                "INCONFORMIDADE"
                                              ? "red"
                                              : "default"
                                        }
                                      >
                                        {
                                          STATUS_ALTERNATIVA_RAM[
                                            item.alternativa.tipo!
                                          ]
                                        }
                                      </Tag>
                                    )}
                                  </div>
                                </div>

                                {item.dataResposta && item.alternativa && (
                                  <Row>
                                    <Col xs={24}>
                                      <Typography.Text type="secondary">
                                        Data Resposta:{" "}
                                        {dayjs(item.dataResposta).format(
                                          "DD/MM/YYYY"
                                        )}
                                      </Typography.Text>
                                    </Col>
                                  </Row>
                                )}

                                <Row gutter={16}>
                                  <Col xs={24} sm={12}>
                                    <Form.Item label="Alternativa Selecionada">
                                      <Input
                                        disabled
                                        value={item.alternativa?.nome}
                                      />
                                    </Form.Item>
                                  </Col>

                                  {item.resposta && (
                                    <Col xs={24}>
                                      <Form.Item label="Observação">
                                        <Input.TextArea
                                          disabled
                                          rows={3}
                                          value={item.resposta}
                                        />
                                      </Form.Item>
                                    </Col>
                                  )}

                                  {/* Anexos da pergunta */}
                                  {item.anexos && item.anexos.length > 0 && (
                                    <Col xs={24}>
                                      <Form.Item label="Anexos">
                                        <List
                                          size="small"
                                          bordered
                                          dataSource={item.anexos}
                                          renderItem={(anexo, anexoIndex) => (
                                            <List.Item
                                              key={anexoIndex}
                                              actions={[
                                                <Button
                                                  key="download"
                                                  type="link"
                                                  size="small"
                                                  icon={<DownloadOutlined />}
                                                  onClick={() =>
                                                    handleOpenAnexo(
                                                      anexo.nomeUpload
                                                    )
                                                  }
                                                >
                                                  Baixar
                                                </Button>,
                                              ]}
                                            >
                                              <List.Item.Meta
                                                avatar={<FileOutlined />}
                                                title={
                                                  <Typography.Text>
                                                    {anexo.nomeArquivo?.split(
                                                      "_"
                                                    )[1] ||
                                                      anexo.nomeArquivo ||
                                                      "Anexo"}
                                                  </Typography.Text>
                                                }
                                                description={
                                                  <Typography.Text
                                                    type="secondary"
                                                    style={{ fontSize: 12 }}
                                                  >
                                                    Arquivo: {anexo.nomeUpload}
                                                  </Typography.Text>
                                                }
                                              />
                                            </List.Item>
                                          )}
                                        />
                                      </Form.Item>
                                    </Col>
                                  )}
                                </Row>
                              </div>
                            ))}
                        </>
                      ),
                    })
                  )}
                />
              </Col>
            </Row>
          </>
        )}

        <Divider orientation="left" plain>
          <Typography.Title level={5}>Conclusão e Ação Fiscal</Typography.Title>
        </Divider>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Form.Item label="Conclusão">
              <Input.TextArea disabled rows={3} value={relatorio?.conclusao} />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Ação Fiscal">
              <Input.TextArea
                disabled
                rows={3}
                value={relatorio?.acaoFiscal || ""}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Assinaturas */}
        {relatorio?.assinaturas && relatorio.assinaturas.length > 0 && (
          <>
            <Divider orientation="left" plain>
              <Typography.Title level={5}>Assinaturas</Typography.Title>
            </Divider>

            <Row gutter={[16, 16]}>
              {relatorio.assinaturas.map((assinatura, index) => (
                <Col xs={24} key={index}>
                  <div
                    style={{
                      marginBottom: 24,
                      padding: 16,
                      border: "1px solid #f0f0f0",
                      borderRadius: 6,
                    }}
                  >
                    <Typography.Title level={5} style={{ marginBottom: 16 }}>
                      Assinante {index + 1}{" "}
                      {assinatura.externo && <Tag color="orange">Externo</Tag>}
                    </Typography.Title>

                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Form.Item label="Nome">
                          <Input disabled value={assinatura.nome} />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={8}>
                        <Form.Item label="CPF">
                          <Input
                            disabled
                            value={cpfMask(assinatura.cpf || "")}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={8}>
                        <Form.Item label="Função">
                          <Input disabled value={assinatura.funcao} />
                        </Form.Item>
                      </Col>

                      {!assinatura.externo && (
                        <Col xs={24} sm={8}>
                          <Form.Item label="Matrícula">
                            <Input disabled value={assinatura.matricula} />
                          </Form.Item>
                        </Col>
                      )}

                      <Col xs={24} sm={assinatura.externo ? 16 : 8}>
                        <Form.Item label="Status da Assinatura">
                          <Input
                            disabled
                            value={getStatusMap(assinatura.statusAssinatura)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {assinatura.statusAssinatura === "ASSINADO_DIGITALMENTE" &&
                      assinatura.assinatura && (
                        <Row justify="center" style={{ marginTop: 16 }}>
                          <Col xs={24} md={12}>
                            <div
                              style={{
                                border: "1px solid #ccc",
                                borderRadius: 8,
                                padding: 8,
                                textAlign: "center",
                                height: 200,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={assinatura.assinatura}
                                alt={`Assinatura de ${assinatura.nome}`}
                                style={{ maxWidth: "100%", maxHeight: "100%" }}
                              />
                            </div>
                            <Typography.Text
                              type="secondary"
                              style={{
                                display: "block",
                                textAlign: "center",
                                marginTop: 8,
                              }}
                            >
                              Assinatura Digital de {assinatura.nome}
                            </Typography.Text>
                          </Col>
                        </Row>
                      )}
                  </div>
                </Col>
              ))}
            </Row>
          </>
        )}

        <Divider />

        <Row justify="center" gutter={[10, 10]}>
          <Col>
            <Button
              htmlType="button"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ width: 200 }}
            >
              Voltar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
